import * as React from "react";
import { AgencyId, AgencyMemberId } from "../../messages/ObjectIds";
import { STRINGS } from "../../strings";
import { AgencyWithChatOnlyDetails } from "../../system/AppState/AppAgenciesState";
import { AppCaregiver } from "../../system/AppState/AppCaregiverState";
import { translate } from "../../translate";
import { HashMap } from "../../utils/HashMap";
import AgenciesListActivity from "../AgenciesList/AgenciesListActivity";
import EmptyScaffoldBody from "../EmptyScaffoldBody/EmptyScaffoldBody";
import ScaffoldTitle from "../Scaffold/components/ScaffoldTitle";
import Scaffold from "../Scaffold/Scaffold";
import { AreYouWorkingWithAnAgencyBottomSheet } from "./components/AreYouWorkingWithAnAgencyBottomSheet";
import { RequestAgencyMembership } from "./components/RequestAgencyMembership";

export interface MyAgenciesScreenProps {
  agencies: HashMap<AgencyId, AgencyWithChatOnlyDetails>;
  caregiver: AppCaregiver;
  isWorkingWithAgencyBottomSheetOpen: boolean;
  onRequestCloseBottomSheet: () => void;
  onClickRequestAgencyMembership: () => void;
  onClickWorkingWithAnAgency: () => void;
  onClickNotWorkingWithAnAgency: () => void;
  onClickAddAgency: () => void;
  onClickChatWithMedflytAgency: () => void;
  onClickCancelAgency: (agencyId: AgencyId) => void;
  onClickChatWithAgencyMember: (agencyMemberId: AgencyMemberId) => void;
  onClickGoBack: () => void;
}

const MyAgenciesScreen = (props: MyAgenciesScreenProps) => {
  const scaffoldBody = React.useMemo(() => {
    if (props.agencies.isEmpty()) {
      return (
        <EmptyScaffoldBody
          label={translate(STRINGS.THERE_ARE_NO_AGENCIES_YET)}
          linkText={translate(STRINGS.REQUEST_AGENCIES_MEMBERSHIP)}
          onClickLink={props.onClickAddAgency}
        />
      );
    }

    return (
      <>
        <AgenciesListActivity
          agencies={props.agencies}
          onClickChatWithAgencyMember={props.onClickChatWithAgencyMember}
          onClickChatWithMedflytAgency={props.onClickChatWithMedflytAgency}
          onClickCancelAgency={props.onClickCancelAgency}
        />
        <AreYouWorkingWithAnAgencyBottomSheet
          isOpen={props.isWorkingWithAgencyBottomSheetOpen}
          onRequestDismiss={props.onRequestCloseBottomSheet}
          onClickYes={props.onClickWorkingWithAnAgency}
          onClickNo={props.onClickNotWorkingWithAnAgency}
        />
      </>
    );
  }, [
    props.agencies,
    props.isWorkingWithAgencyBottomSheetOpen,
    props.onClickAddAgency,
    props.onClickCancelAgency,
    props.onClickChatWithAgencyMember,
    props.onClickChatWithMedflytAgency,
    props.onClickNotWorkingWithAnAgency,
    props.onClickWorkingWithAnAgency,
    props.onRequestCloseBottomSheet,
  ]);

  return (
    <Scaffold
      title={<ScaffoldTitle label={translate(STRINGS.MY_AGENCIES)} />}
      extraHeader={<RequestAgencyMembership onClick={props.onClickRequestAgencyMembership} />}
      className="MyAgenciesScreen"
      onClickGoBack={props.onClickGoBack}
    >
      {scaffoldBody}
    </Scaffold>
  );
};

export default React.memo(MyAgenciesScreen);
